import React from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import {
  Wrench, Truck, ShieldCheck, MapPin, Star, Bell, ArrowLeft,
  Car, ClipboardList, Building2, Gauge, Mail, Phone,
} from "lucide-react";
import { CitiesIntro, HomeFaq } from "@/components/HomeSeoSections";

const IMG = {
  hero: "/hero.webp",
};

const STEPS = [
  { icon: ClipboardList, t: "أرسل طلب صيانة", d: "صف مشكلة سيارتك وحدد الخدمة والموقع" },
  { icon: Building2, t: "استقبل عروض الورش", d: "قارن الأسعار والضمان ومدة الإصلاح" },
  { icon: Truck, t: "اطلب سطحة عند الحاجة", d: "إذا كانت السيارة متعطلة اطلب سحبها فوراً" },
  { icon: Gauge, t: "تابع الإصلاح والاستلام", d: "تتبع حالة سيارتك خطوة بخطوة" },
];

const FEATURES = [
  { icon: Building2, t: "شبكة ورش موثوقة", d: "ورش متخصصة في مدينتك حسب نوع الخدمة" },
  { icon: Truck, t: "سطحات قريبة", d: "عروض سحب فورية مع تتبع مباشر على الخريطة" },
  { icon: MapPin, t: "تتبع مباشر", d: "اعرف موقع السطحة وحالة طلبك لحظة بلحظة" },
  { icon: Star, t: "تقييمات شفافة", d: "اختر الأفضل بناءً على تقييمات العملاء" },
  { icon: Bell, t: "إشعارات فورية", d: "تنبيهات عند كل تحديث لطلبك" },
  { icon: ShieldCheck, t: "ضمان على العمل", d: "عروض واضحة تشمل الضمان على القطع والعمل" },
];

export default function Landing() {
  const navigate = useNavigate();
  return (
    <div className="min-h-screen bg-background overflow-x-hidden">
      {/* Nav */}
      <header className="sticky top-0 z-30 glass border-b border-border">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src="/logo.webp" alt="شعار إسناد كار لصيانة السيارات وطلب السطحات" width="40" height="40" loading="lazy" className="w-10 h-10 rounded-xl object-cover shadow-lg" data-testid="app-logo" />
            <span className="font-heading font-extrabold text-xl">اسناد كار</span>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="ghost" className="rounded-full" onClick={() => navigate("/login")} data-testid="nav-login">
              تسجيل الدخول
            </Button>
            <Button className="rounded-full" onClick={() => navigate("/register")} data-testid="nav-register">
              إنشاء حساب
            </Button>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="relative hero-grid">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-16 lg:py-24 grid lg:grid-cols-2 gap-12 items-center">
          <div className="animate-fade-up">
            <span className="inline-flex items-center gap-2 rounded-full bg-accent/10 text-accent px-4 py-1.5 text-sm font-semibold border border-accent/20">
              <Truck className="w-4 h-4" /> صيانة + سطحة في منصة واحدة
            </span>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-[1.35] mt-6">
              مشكلة في سيارتك؟
              <span className="text-primary"> نوصّلك بأفضل الورش والسطحات</span>
            </h1>
            <p className="text-lg text-muted-foreground mt-6 leading-relaxed max-w-xl">
              أرسل طلب صيانة واحد، واستقبل عروض أسعار من عدة ورش، قارن واختر الأنسب لك. وإذا كانت
              سيارتك متعطلة اطلب سطحة وتابعها مباشرة حتى الوصول للورشة.
            </p>
            <div className="flex flex-wrap gap-3 mt-8">
              <Button size="lg" className="rounded-full text-base h-13 px-8 gap-2 group" onClick={() => navigate("/register")} data-testid="hero-cta">
                ابدأ الآن مجاناً
                <ArrowLeft className="w-5 h-5 transition-transform group-hover:-translate-x-1" />
              </Button>
              <Button size="lg" variant="outline" className="rounded-full text-base h-13 px-8" onClick={() => navigate("/login")} data-testid="hero-login">
                لديّ حساب
              </Button>
            </div>
            <div className="flex items-center gap-6 mt-8 text-sm text-muted-foreground">
              <div className="flex items-center gap-2"><Building2 className="w-4 h-4 text-primary" /> ورش موثوقة</div>
              <div className="flex items-center gap-2"><Truck className="w-4 h-4 text-accent" /> سطحات قريبة</div>
              <div className="flex items-center gap-2"><Star className="w-4 h-4 text-amber-400" /> تقييمات حقيقية</div>
            </div>
          </div>
          <div className="relative animate-fade-up" style={{ animationDelay: "120ms" }}>
            <div className="absolute -inset-4 bg-primary/10 rounded-[2rem] blur-2xl" />
            <img src={IMG.hero} alt="سيارة في ورشة صيانة معتمدة عبر منصة إسناد كار" width="1264" height="848" fetchpriority="high" className="relative rounded-[2rem] w-full h-[360px] object-cover shadow-[0_20px_60px_rgba(0,0,0,0.18)] border border-border" />
            <div className="absolute -bottom-6 -start-4 glass rounded-2xl p-4 border border-border shadow-lg flex items-center gap-3">
              <div className="w-11 h-11 rounded-xl bg-accent flex items-center justify-center text-white"><Truck className="w-5 h-5" /></div>
              <div>
                <div className="text-sm font-bold">السطحة في الطريق</div>
                <div className="text-xs text-muted-foreground">وقت الوصول ~ 12 دقيقة</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <CitiesIntro />

      {/* Steps */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 py-16">
        <h2 className="font-heading text-2xl sm:text-3xl font-extrabold text-center mb-3">كيف تعمل المنصة؟</h2>
        <p className="text-muted-foreground text-center mb-12">تجربة بسيطة من الطلب حتى استلام سيارتك</p>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {STEPS.map((s, i) => (
            <div key={i} className="relative bg-card border border-border rounded-2xl p-6 transition-transform hover:-translate-y-1">
              <div className="absolute top-4 end-4 font-heading text-5xl font-extrabold text-primary/10">{i + 1}</div>
              <div className="w-12 h-12 rounded-xl bg-primary/10 text-primary flex items-center justify-center mb-4">
                <s.icon className="w-6 h-6" />
              </div>
              <div className="font-bold text-lg mb-1">{s.t}</div>
              <div className="text-sm text-muted-foreground leading-relaxed">{s.d}</div>
            </div>
          ))}
        </div>
      </section>

      {/* Features */}
      <section className="bg-card border-y border-border">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-16 grid lg:grid-cols-3 gap-6">
          {FEATURES.map((f, i) => (
            <div key={i} className="flex gap-4 p-4">
              <div className="w-12 h-12 shrink-0 rounded-xl bg-accent/10 text-accent flex items-center justify-center">
                <f.icon className="w-6 h-6" />
              </div>
              <div>
                <div className="font-bold text-lg">{f.t}</div>
                <div className="text-sm text-muted-foreground leading-relaxed mt-1">{f.d}</div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Roles CTA */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 py-16">
        <div className="grid lg:grid-cols-3 gap-6">
          {[
            { icon: Car, t: "أنا عميل", d: "أطلب صيانة وسطحة لسيارتي", role: "customer", color: "from-primary to-slate-900" },
            { icon: Building2, t: "أنا ورشة", d: "أستقبل الطلبات وأرسل عروض الأسعار", role: "workshop", color: "from-emerald-500 to-emerald-700" },
            { icon: Truck, t: "أنا مقدم سطحة", d: "أستقبل طلبات السحب القريبة", role: "tow_provider", color: "from-accent to-amber-600" },
          ].map((r) => (
            <div key={r.role} onClick={() => navigate(`/register?role=${r.role}`)} data-testid={`role-card-${r.role}`}
              className="cursor-pointer group bg-card border border-border rounded-2xl p-8 transition-transform hover:-translate-y-1">
              <div className={`w-14 h-14 rounded-2xl bg-gradient-to-br ${r.color} text-white flex items-center justify-center mb-5 shadow-lg`}>
                <r.icon className="w-7 h-7" />
              </div>
              <div className="font-heading font-extrabold text-xl mb-2">{r.t}</div>
              <p className="text-muted-foreground">{r.d}</p>
              <div className="mt-4 text-primary font-semibold flex items-center gap-2 group-hover:gap-3 transition-all">
                سجّل الآن <ArrowLeft className="w-4 h-4" />
              </div>
            </div>
          ))}
        </div>
      </section>

      <HomeFaq />

      <footer className="border-t border-border">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8 space-y-4 text-center text-sm text-muted-foreground">
          <div className="flex flex-wrap items-center justify-center gap-x-8 gap-y-3">
            <a href="mailto:isnadcar@isnadcar.sa" className="flex items-center gap-2 hover:text-primary transition-colors" data-testid="contact-email">
              <Mail className="w-4 h-4" /> isnadcar@isnadcar.sa
            </a>
            <a href="tel:+966576463324" dir="ltr" className="flex items-center gap-2 hover:text-primary transition-colors" data-testid="contact-phone">
              <Phone className="w-4 h-4" /> 0576463324
            </a>
          </div>
          <div>اسناد كار © 2026 — منصة صيانة وعروض أسعار وسحب المركبات</div>
        </div>
      </footer>
    </div>
  );
}
